import { Key } from "react";
import { createSlice } from "@reduxjs/toolkit";
import { menuItemService } from "./services/menuItemService";
import { Portion } from "./setMenuSlice";

export interface MenuItem {
    portionLabel?: any;
    calculatedPrice?: any;
    isAvailable: boolean | undefined;
    name?: string | undefined;
    id: string;
    category?: any;
    description?: string;
    displayName: string;
    imageUrl: any;
    mealType?: string;
    preparingTime?: number;
    price: number;
    overridePrice?: number;
    restaurantId: string;
    isCustomMenuItem?: boolean;
    customMenuItemSection?: string;
    createdAt: {
        _nanoseconds: number;
        _seconds: number;
    };
    itemType?: String;
    itemId?: String;
    quantity?: number;
    availableDays: any;
    supportsPreOrdering?: boolean;
    supportPortionSizes?: boolean;
    portions?: Portion[];
    allergenInformation?: string[];
    foodAttributes?: string[];
    availableItemCount?: number;
    items?: any[];
    status: string;
    updatedAt?: any[];
    extraItems?: any[];
    priceType?: string;
    sections?: any;
    totalItems?: number;
    customItemType?: string;
    menuItemId?: string;
    unitPrice?: number;
    isCooporateProduct?: boolean;
    organizationIds?: string[];
    dimensions?: { [dimensionId: string]: { dimensionValueId: string } };
    customItems?: any[];
    customVariantKey?: string;
    menuId?: string | null;
    isAddOn?: boolean;
    customItemTotal?: number;
    perItemManualDiscount?: number;
    productId?: string;
    isSellingProduct?: boolean;
}

export interface MenuCategory {
    description: string;
    preparingTime: any;
    price: any;
    displayName: string;
    category: any;
    items: MenuItem[];
}

export interface GetMenuItemsResponse {
    [x: string]: any;

    items: any;
    category: Key | null | undefined;
    message: string;
    data: MenuCategory[];
}

export interface CreateMenuItemRequest {
    category: string | any;
    description: string;
    displayName: string;
    imageUrl: string[];
    mealType: string;
    preparingTime: any;
    price: number;
    restaurantId: string;
    imgData: string[];
    isCustomMenuItem?: boolean;
    customMenuItemSection?: string;
}

export interface CreateMenuItem {
    category: any;
    description: string;
    displayName: string;
    abbreviation?: string;
    imageUrl: string[];
    mealType: string;
    preparingTime: any;
    price: any;
    restaurantId: string;
    isCustomMenuItem?: boolean;
    customMenuItemTypeId?: string;
    customMenuItemSection?: string;
    supportsPreOrdering?: boolean;
    supportPortionSizes?: boolean;
    portions?: Portion[];
    allergenInformation?: string[];
    foodAttributes?: string[];
    portionSize?: string;
    portionPrice?: number;
    filterTags?: Array<{ value: string; label: React.ReactNode }>;
    // filterTags?: string[];
    productId: string;
    tags: any;
    isCooporateProduct ?: boolean;
    organizationIds ?: string[];
    dimensions?: { [dimensionId: string]: { dimensionValueId: string } };
    tinyId?:number;
    isSellingProduct ?: boolean;
    skuId ?: string;
}

export interface UpdateMenuItemRequest {
    menuItemId: string;
    category?: string;
    description?: string;
    displayName?: string;
    mealType?: string;
    preparingTime?: number;
    price?: number;
}

export interface SingleMenuItem {
    label: string;
    price: number;
    itemId: string;
    itemType: string;
    description: string;
}

export interface SingleMenuItemsApiResponse {
    menuItems: SingleMenuItem[];
}

export interface AvailabilityStatus {
    restaurantId: string;
    menuId: string;
    menuType: string;
    status: boolean;
    isArchived?: boolean;
}

export interface UpdateMenuItemAvailabilityRequest {
    restaurantId: string;
    menuType: string;
    menuItemsData: {
        itemId: string;
        availableDays: number[];
    }[];
}

export interface AllergenInformation {
    displayName: string;
    imageUrl: string;
}

export interface GetAllergenInformationResponse {
    allergenInformation: AllergenInformation[];
}

export interface FoodAttribute {
    displayName: string;
    imageUrl: string;
}

export interface GetFoodAttributesListResponse {
    FoodAttributesList: FoodAttribute[];
}

export interface FoodAttribute {
    displayName: string;
    imageUrl: string;
}

export interface GetFoodAttributesListResponse {
    FoodAttributesList: FoodAttribute[];
}

export interface FilterTag {
    show: boolean;
    tag: string;
}

export interface CategoryFilter {
    allowMultipleSelect: boolean;
    tags: FilterTag[];
}

export interface FilterTagsResponse {
    filterTags: {
        [category: string]: CategoryFilter;
    };
}

interface IntialStateInterface {
    menuItems: any[];
    loading: boolean;
    error: any;
    allMenuItemsRowsPerPage: number;
    allMenuItemsCustomizedMenuItemsRowsPerPage: number;
}

const initialState: IntialStateInterface = {
    menuItems: [],
    loading: false,
    error: null,
    allMenuItemsRowsPerPage: 10,
    allMenuItemsCustomizedMenuItemsRowsPerPage: 10,
};

const menuSlice = createSlice({
    name: "menu",
    initialState,
    reducers: {
        getMenuItemsStart(state) {
            state.loading = true;
            state.error = null;
        },
        getMenuItemsSuccess(state, action) {
            state.menuItems = action.payload;
            state.loading = false;
            state.error = null;
        },
        getMenuItemsFailure(state, action) {
            state.loading = false;
            state.error = action.payload;
        },
        setAllMenuItemsRowsPerPage(state, action) {
            state.allMenuItemsRowsPerPage = action.payload;
        },
        clearAllMenuItemsRowsPerPage(state) {
            state.allMenuItemsRowsPerPage = 10;
        },
        setAllCustomizedMenuItemsRowsPerPage(state, action) {
            state.allMenuItemsCustomizedMenuItemsRowsPerPage = action.payload;
        },
        clearAllCustomizedMenuItemsRowsPerPage(state) {
            state.allMenuItemsCustomizedMenuItemsRowsPerPage = 10;
        },
        setSingleItems(state, action) {
            state.menuItems = action.payload;
        },
    },

    extraReducers: (builder) => {
        builder.addMatcher(
            menuItemService.endpoints.getMenuItemsByRestaurantId.matchFulfilled,
            (state, { payload }: { payload: any }) => {
                state = { ...state, menuItems: [...payload] };
                return state;
            }
        );
    },
});

export const { getMenuItemsStart, getMenuItemsSuccess, getMenuItemsFailure, setAllMenuItemsRowsPerPage, clearAllMenuItemsRowsPerPage, setAllCustomizedMenuItemsRowsPerPage, clearAllCustomizedMenuItemsRowsPerPage, setSingleItems } =
    menuSlice.actions;

export default menuSlice.reducer;
